import useFetchItem from '../useFetchItem.js';
import useMutateItem from '../useMutateItem.js';
import HttpService from '../httpServices';
import { routes } from '../apiroutes';
import { ErrorHandler } from '../errorHandler';

const httpService = new HttpService();

/**
 * Overtime Service
 * Handles all overtime-related API calls and data management
 */

// Overtime Reports Hook
export const useOvertimeReports = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['overtimeReports', options.filters],
    queryFn: (params) => {
      const url = routes.getOvertimeReports(options.filters || {});
      return httpService.getData(url);
    },
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: options.staleTime || 5 * 60 * 1000, // 5 minutes default
    cacheTime: options.cacheTime || 10 * 60 * 1000, // 10 minutes default
    refetchOnWindowFocus: options.refetchOnWindowFocus !== false
  });

  return {
    overtimeReportsData: data?.data?.overtime_reports || data?.overtime_reports || [],
    overtimeReportsError: ErrorHandler(error),
    overtimeReportsIsLoading: isLoading,
    refetchOvertimeReports: refetch,
  };
};

// Overtime Summary Hook
export const useOvertimeSummary = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['overtimeSummary', options.filters],
    queryFn: (params) => {
      const url = routes.getOvertimeSummary(options.filters || {});
      return httpService.getData(url);
    },
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: options.staleTime || 5 * 60 * 1000,
    cacheTime: options.cacheTime || 10 * 60 * 1000,
    refetchOnWindowFocus: options.refetchOnWindowFocus !== false
  });

  return {
    overtimeSummaryData: data?.data || data || {},
    overtimeSummaryError: ErrorHandler(error),
    overtimeSummaryIsLoading: isLoading,
    refetchOvertimeSummary: refetch,
  };
};

// Attendance with Overtime Hook
export const useAttendanceWithOvertime = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['attendanceWithOvertime', options.filters],
    queryFn: (params) => {
      const url = routes.getAttendanceWithOvertime(options.filters || {});
      return httpService.getData(url);
    },
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: options.staleTime || 5 * 60 * 1000,
    cacheTime: options.cacheTime || 10 * 60 * 1000,
    refetchOnWindowFocus: options.refetchOnWindowFocus !== false
  });

  return {
    attendanceWithOvertimeData: data?.data?.attendance_records || data?.attendance_records || [],
    attendanceWithOvertimeError: ErrorHandler(error),
    attendanceWithOvertimeIsLoading: isLoading,
    refetchAttendanceWithOvertime: refetch,
  };
};

// Clock-in Hook (for mobile)
export const useClockIn = (onSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) => httpService.postData(payload, routes.clockIn()),
    onSuccess: (response) => {
      const resData = response?.data || {};
      if (onSuccess) onSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      console.error('Clock-in error:', error);
    },
  });

  return {
    clockInData: data,
    clockInError: ErrorHandler(error),
    clockInIsLoading: isPending,
    clockInPayload: (requestPayload) => mutate(requestPayload),
  };
};

// Clock-out Hook (for mobile)
export const useClockOut = (onSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: ({ employeeId, payload }) => httpService.putData(payload, routes.clockOut(employeeId)),
    onSuccess: (response) => {
      const resData = response?.data || {};
      if (onSuccess) onSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      console.error('Clock-out error:', error);
    },
  });

  return {
    clockOutData: data,
    clockOutError: ErrorHandler(error),
    clockOutIsLoading: isPending,
    clockOutPayload: (employeeId, requestPayload) => mutate({ employeeId, payload: requestPayload }),
  };
};

// Clock Break Hook (for mobile)
export const useClockBreak = (onSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) => httpService.postData(payload, routes.clockBreak()),
    onSuccess: (response) => {
      const resData = response?.data || {};
      if (onSuccess) onSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      console.error('Clock break error:', error);
    },
  });

  return {
    clockBreakData: data,
    clockBreakError: ErrorHandler(error),
    clockBreakIsLoading: isPending,
    clockBreakPayload: (requestPayload) => mutate(requestPayload),
  };
};

  // Admin Overtime Reports Hook (for company admins with branch filtering)
  export const useAdminOvertimeReports = (options = {}) => {
    const { data, error, isLoading, refetch } = useFetchItem({
      queryKey: ['adminOvertimeReports', options.filters, options.branch_id],
      queryFn: (params) => {
        const filters = { ...options.filters };
        
        // Add branch_id to filters if provided and not 'all' or undefined
        if (options.branch_id && options.branch_id !== 'all' && options.branch_id !== undefined) {
          filters.branch_id = options.branch_id;
        }
        
        const url = routes.getAdminOvertimeReports(filters);
        return httpService.getData(url);
      },
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: 0, // Always refetch when query key changes
    cacheTime: options.cacheTime || 10 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  return {
    adminOvertimeReportsData: data?.data?.overtime_reports || data?.overtime_reports || [],
    adminOvertimeReportsError: ErrorHandler(error),
    adminOvertimeReportsIsLoading: isLoading,
    refetchAdminOvertimeReports: refetch,
  };
};

  // Admin Overtime Summary Hook (for company admins with branch filtering)
  export const useAdminOvertimeSummary = (options = {}) => {
    const { data, error, isLoading, refetch } = useFetchItem({
      queryKey: ['adminOvertimeSummary', options.filters, options.branch_id],
      queryFn: (params) => {
        const filters = { ...options.filters };
        
        // Add branch_id to filters if provided and not 'all' or undefined
        if (options.branch_id && options.branch_id !== 'all' && options.branch_id !== undefined) {
          filters.branch_id = options.branch_id;
        }
        
        const url = routes.getAdminOvertimeSummary(filters);
        return httpService.getData(url);
      },
    enabled: options.enabled !== false,
    retry: 2,
    staleTime: 0, // Always refetch when query key changes
    cacheTime: options.cacheTime || 10 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  return {
    adminOvertimeSummaryData: data?.data || data || {},
    adminOvertimeSummaryError: ErrorHandler(error),
    adminOvertimeSummaryIsLoading: isLoading,
    refetchAdminOvertimeSummary: refetch,
  };
};

// Combined Overtime Analytics Hook
export const useOvertimeAnalytics = (options = {}) => {
  // Load overtime summary first
  const overtimeSummary = useOvertimeSummary({ 
    enabled: options.enabled,
    filters: options.filters,
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  // Load overtime reports after summary
  const overtimeReports = useOvertimeReports({ 
    enabled: options.enabled && !!overtimeSummary.overtimeSummaryData,
    filters: options.filters,
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  return {
    // Summary data
    summaryData: overtimeSummary.overtimeSummaryData,
    summaryError: overtimeSummary.overtimeSummaryError,
    summaryIsLoading: overtimeSummary.overtimeSummaryIsLoading,
    refetchSummary: overtimeSummary.refetchOvertimeSummary,

    // Reports data
    reportsData: overtimeReports.overtimeReportsData,
    reportsError: overtimeReports.overtimeReportsError,
    reportsIsLoading: overtimeReports.overtimeReportsIsLoading,
    refetchReports: overtimeReports.refetchOvertimeReports,

    // Combined loading state
    isLoading: overtimeSummary.overtimeSummaryIsLoading,
    isReportsLoading: overtimeReports.overtimeReportsIsLoading,

    // Combined error state
    hasError: overtimeSummary.overtimeSummaryError || overtimeReports.overtimeReportsError,

    // Refetch all data
    refetchAll: () => {
      overtimeSummary.refetchOvertimeSummary();
      overtimeReports.refetchOvertimeReports();
    },
  };
};

// Combined Admin Overtime Analytics Hook (for company admins)
export const useAdminOvertimeAnalytics = (options = {}) => {
  // Load admin overtime summary first
  const adminOvertimeSummary = useAdminOvertimeSummary({ 
    enabled: options.enabled,
    filters: options.filters,
    branch_id: options.branch_id, // Pass branch_id for filtering
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  // Load admin overtime reports after summary
  const adminOvertimeReports = useAdminOvertimeReports({ 
    enabled: options.enabled && !!adminOvertimeSummary.adminOvertimeSummaryData,
    filters: options.filters,
    branch_id: options.branch_id, // Pass branch_id for filtering
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  return {
    // Summary data
    summaryData: adminOvertimeSummary.adminOvertimeSummaryData,
    summaryError: adminOvertimeSummary.adminOvertimeSummaryError,
    summaryIsLoading: adminOvertimeSummary.adminOvertimeSummaryIsLoading,
    refetchSummary: adminOvertimeSummary.refetchAdminOvertimeSummary,

    // Reports data
    reportsData: adminOvertimeReports.adminOvertimeReportsData,
    reportsError: adminOvertimeReports.adminOvertimeReportsError,
    reportsIsLoading: adminOvertimeReports.adminOvertimeReportsIsLoading,
    refetchReports: adminOvertimeReports.refetchAdminOvertimeReports,

    // Combined loading state
    isLoading: adminOvertimeSummary.adminOvertimeSummaryIsLoading,
    isReportsLoading: adminOvertimeReports.adminOvertimeReportsIsLoading,

    // Combined error state
    hasError: adminOvertimeSummary.adminOvertimeSummaryError || adminOvertimeReports.adminOvertimeReportsError,

    // Refetch all data
    refetchAll: () => {
      adminOvertimeSummary.refetchAdminOvertimeSummary();
      adminOvertimeReports.refetchAdminOvertimeReports();
    },
  };
};

// Utility functions for overtime calculations
export const overtimeUtils = {
  // Calculate overtime type based on hours
  getOvertimeType: (shiftHours, totalHours) => {
    if (totalHours <= shiftHours) return 'none';
    if (totalHours > shiftHours + 2) return 'full_overtime';
    return 'partial_overtime';
  },

  // Format overtime hours for display
  formatOvertimeHours: (hours) => {
    if (!hours || hours <= 0) return '-';
    return `${hours.toFixed(2)}h`;
  },

  // Get overtime type display name
  getOvertimeTypeDisplay: (type) => {
    const types = {
      'early_clockin': 'Early Clock-in',
      'late_clockout': 'Late Clock-out',
      'full_overtime': 'Full Overtime',
      'partial_overtime': 'Partial Overtime',
      'none': 'None'
    };
    return types[type] || type;
  },

  // Get overtime type color for badges
  getOvertimeTypeColor: (type) => {
    const colors = {
      'early_clockin': 'blue',
      'late_clockout': 'orange',
      'full_overtime': 'red',
      'partial_overtime': 'yellow',
      'none': 'gray'
    };
    return colors[type] || 'gray';
  },

  // Check if employee has overtime
  hasOvertime: (record) => {
    return record.overtime_hours > 0 || record.overtime_type !== 'none';
  },

  // Calculate total overtime hours from records
  calculateTotalOvertime: (records) => {
    return records.reduce((total, record) => total + (record.overtime_hours || 0), 0);
  }
}; 